import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './3SCDocumentUpload.module.css';

// --- Helper Icon Components ---
const UploadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="17 8 12 3 7 8"></polyline>
        <line x1="12" y1="3" x2="12" y2="15"></line>
    </svg>
);
const CheckCircleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
    </svg>
);

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB limit enforced by backend
const ACCEPTED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png'
];

const uploadThreeSCDocumentFile = async (token, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', 'document');

    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/core/s3/upload/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
    });

    if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || 'File upload failed');
    }

    return res.json();
};

const DocumentUploadStep2 = () => {
    // First fetch all the ThreeSCAnswers for the current user
    const [threeSCAnswers, setThreeSCAnswers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Prevent double fetch in StrictMode/dev by using a ref flag
    const hasFetchedAnswers = React.useRef(false);
    useEffect(() => {
        if (hasFetchedAnswers.current) return;
        hasFetchedAnswers.current = true;
        const fetchAnswers = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                if (!token) { navigate('/login'); return; }
                const res = await fetch(process.env.REACT_APP_API_BASE_URL + '/api/threesc/fetch-answers/', {
                    method: 'GET',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                });
                if (res.status === 401) { navigate('/login'); return; }
                const data = await res.json();
                setThreeSCAnswers(data);
            } catch (e) { 
                console.error('Failed to fetch answers:', e); 
            }
            finally { setLoading(false); }
        };
        fetchAnswers();
    }, []);

    const navigate = useNavigate();
    // Track uploaded documents (S3 metadata) per answer
    const [uploadedDocs, setUploadedDocs] = useState({});
    const [uploadingByAnswerId, setUploadingByAnswerId] = useState({});
    const [uploadErrors, setUploadErrors] = useState({});
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [missingByAnswerId, setMissingByAnswerId] = useState({});
    const [submitError, setSubmitError] = useState('');

    const handleFileChange = async (e, answerId) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
            setUploadErrors(prev => ({
                ...prev,
                [answerId]: 'Unsupported file type. Upload PDF, DOC, XLS, JPG, or PNG.'
            }));
            return;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            setUploadErrors(prev => ({
                ...prev,
                [answerId]: 'File exceeds 10MB limit.'
            }));
            return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        setUploadErrors(prev => ({ ...prev, [answerId]: null }));
        setUploadingByAnswerId(prev => ({ ...prev, [answerId]: true }));
        try {
            const response = await uploadThreeSCDocumentFile(token, file);
            setUploadedDocs(prev => ({
                ...prev,
                [answerId]: {
                    fileId: response.file_id,
                    fileUrl: response.file_url,
                    fileName: response.filename || file.name,
                    fileSize: response.file_size || file.size
                }
            }));
        } catch (error) {
            console.error('Failed to upload file for answer', answerId, error);
            setUploadErrors(prev => ({
                ...prev,
                [answerId]: error.message || 'Upload failed. Try again.'
            }));
        } finally {
            setUploadingByAnswerId(prev => ({ ...prev, [answerId]: false }));
        }
    };
    
    const handleRemoveFile = (answerId) => {
         setUploadedDocs(prev => {
            const updated = { ...prev };
            delete updated[answerId];
            return updated;
        });
        setUploadErrors(prev => {
            const updated = { ...prev };
            delete updated[answerId];
            return updated;
        });
    };

    const handleSelfAttestedChange = (e, answerId) => {
        const isChecked = e.target.checked;
        setThreeSCAnswers((previousAnswers) =>
            previousAnswers.map((answer) =>
                answer.id === answerId
                    ? { ...answer, self_attested: isChecked }
                    : answer
            )
        );
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        const token = localStorage.getItem('token');
        if (!token) {
            alert('You must be logged in to submit documents.');
            setIsSubmitting(false);
            return;
        }

        setSubmitAttempted(true);
        setSubmitError('');

        // Validate: each answer must have a file or self-declaration
        const missingMap = {};
        threeSCAnswers.forEach((answer) => {
            const hasFile = !!uploadedDocs[answer.id];
            const hasDeclaration = !!answer.self_attested;
            if (!hasFile && !hasDeclaration) {
                missingMap[answer.id] = true;
            }
        });
        setMissingByAnswerId(missingMap);

        if (Object.keys(missingMap).length > 0) {
            setSubmitError('Please upload a document or mark self-declared for all highlighted questions.');
            setIsSubmitting(false);
            return;
        }

        // Build documents_data payload with file IDs returned from S3
        const documentsData = [];
        threeSCAnswers.forEach((answer) => {
            const questionId = (answer?.question && (answer.question.id || answer.question.question_id))
                || answer.question_id
                || answer.question
                || answer.id; // fallback

            const uploadedDoc = uploadedDocs[answer.id] || null;
            const isSelfDeclared = !!answer.self_attested;

            if (!uploadedDoc && !isSelfDeclared) return;

            documentsData.push({
                question_id: questionId,
                self_declaration: isSelfDeclared,
                description: 'Uploaded via Document Uploads UI',
                document_file_id: uploadedDoc?.fileId || null,
                document_file_url: uploadedDoc?.fileUrl || null,
                document_file_name: uploadedDoc?.fileName || null
            });
        });

        if (documentsData.length === 0) {
            setSubmitError('Nothing to submit. Upload a file or mark self-declared for at least one question.');
            setIsSubmitting(false);
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/threesc/documents/upload/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ documents_data: documentsData })
            });

            if (response.ok) {
                setSubmitError('');
                navigate('/sustainability/review'); // Navigate to the next step
            } else {
                if (response.status === 413) {
                    setSubmitError('File size exceeds maximum allowed size of 10MB.');
                } else {
                    const data = await response.json().catch(() => null);
                    console.error('Error uploading documents:', data);
                    setSubmitError((data && data.error) || 'Failed to upload documents. Please try again.');
                }
            }
        } catch (error) {
            console.error('Error uploading documents:', error);
            setSubmitError('An error occurred while uploading documents. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    // Helper function to render dynamic answer upload card
    const renderUploadCard = (answer) => {
        const file = uploadedDocs[answer.id] || null;
        const hasError = submitAttempted && missingByAnswerId[answer.id];
        const isUploading = !!uploadingByAnswerId[answer.id];
        const uploadError = uploadErrors[answer.id];
        return (
            <div key={answer.id} className={`${styles.uploadCard} ${hasError ? styles.error : ''}`}>
                <h5>{
                    typeof answer.question === 'object' && answer.question !== null
                        ? (answer.question.text || JSON.stringify(answer.question))
                        : String(answer.question)
                }</h5>
                {answer.question.document_name && (
                    <p className={styles.documentNote}>
                        Document Name: {answer.question.document_name}
                    </p>
                )}
                <p className={styles.fileTypes}>Accepts: PDF, JPG, PNG, DOC</p>

                {file ? (
                    <div className={styles.filePreview}>
                        <span className={styles.checkIcon}><CheckCircleIcon/></span>
                        <div className={styles.fileMeta}>
                            <span className={styles.fileName}>{file.fileName}</span>
                            {file.fileUrl && (
                                <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" className={styles.previewLink}>
                                    View
                                </a>
                            )}
                        </div>
                        <button onClick={() => handleRemoveFile(answer.id)} className={styles.removeButton}>×</button>
                    </div>
                ) : (
                    <div className={styles.dropzone}>
                        <UploadIcon />
                        <label htmlFor={`answer-${answer.id}`}>Click to upload file</label>
                        <input
                            type="file"
                            id={`answer-${answer.id}`}
                            className={styles.fileInput}
                            onChange={(e) => handleFileChange(e, answer.id)}
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        />
                        {isUploading && <p className={styles.uploadingText}>Uploading...</p>}
                        {uploadError && <p className={styles.errorText}>{uploadError}</p>}
                    </div>
                )}

                <label className={styles.checkboxRow}>
                    <input
                        type="checkbox"
                        className={styles.checkbox}
                        onChange={(e) => handleSelfAttestedChange(e, answer.id)}
                        checked={!!answer.self_attested}
                    />
                    <span className={styles.checkboxLabel}>Self Declaration</span>
                </label>
                {/* Add a New line */}
                <br />
                <span className={styles.checkboxText}>I don’t have supporting documents at this time, but I confirm that I’ve answered to the best of my knowledge and capacity.</span>
                <br />
                <span className={styles.checkboxText}>I understand that not providing proof may affect my certification score.</span>
                {hasError && (
                    <div className={styles.errorText}>Either upload a document or mark self-declared.</div>
                )}
            </div>
        );
    };

    return (
        <div className={styles.uploadPage}>
            <div className={styles.header}>
                <h1>Step 2: Document Uploads</h1>
                <p>Please upload the required documents to verify your sustainability claims.</p>
            </div>

            <main className={styles.mainContent}>
                {/* Dynamic answers mapped as cards */}
                {threeSCAnswers && threeSCAnswers.length > 0 && (
                    <div className={styles.answersList}>
                        {threeSCAnswers.map((answer) => renderUploadCard(answer))}
                    </div>
                )}
                {submitAttempted && submitError && (
                    <div className={styles.errorBanner}>{submitError}</div>
                )}
                <button className={styles.submitButton} onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Submit Proofs & Continue'}
                    {isSubmitting && <div className={styles.spinner}></div>}
                </button>
            </main>
        </div>
    );
};

export default DocumentUploadStep2;